import { useContext, useState } from "react";
import {
  PersonFiltersContext,
  PersonFiltersEventContext,
} from "../../../../context/FiltersContext";
import {
  QueryFilter,
  PersonFilterKeys,
  IPersonUdvalgFilterSetting,
} from "../../../../interfaces/filters";
import { AccordionFilterItem } from "../../../accordion/Accordion";
import AutoCompleteUdvalg from "../../../autocomplete/AutoCompleteUdvalg";
import AppliedFilterItem from "../../../filter/AppliedFilterItem";
import {
  PersonDtoFilterInput,
  PersonUdvalgFilterInput,
} from "../../../../interfaces/schema";
import { f } from "../../../../util/f";
import DateFilter from "../../../filter/date/DateFilter";
import { defaultDateEvent } from "../../../filter/date/dateFilterHelpers";
import { DateFilterEvent } from "../../../filter/date/types";

// helpers
const filterKey: QueryFilter<PersonFilterKeys>["filterKey"] = "personUdvalg";

export default function PersonUdvalgFilter() {
  const filters = useContext(PersonFiltersContext);
  const updateFilters = useContext(PersonFiltersEventContext);

  // find active settings
  const settings: IPersonUdvalgFilterSetting = filters.find(
    (f) => f.filterKey === filterKey,
  )?.settings;

  // state
  const [latestDateEvent, setLatestDateEvent] =
    useState<DateFilterEvent>(defaultDateEvent);

  // stuff
  const isDateDisabled = Boolean(!settings?.activeFilters?.length);

  // handlers
  function updateFilterHandler(
    selectedItem: IUdvalg | null,
    dateFilterEvent?: DateFilterEvent,
  ) {
    // get active list if any otherwise initialise an empty array
    const activeFilters = settings?.activeFilters || [];

    // add selected item if it does not exist in active list
    if (selectedItem && !activeFilters.some((s) => s.id === selectedItem.id)) {
      activeFilters.push({
        id: selectedItem.id,
        name: selectedItem.navn,
      });
    }

    // check for valid date event otherwise assign latest date event
    if (!dateFilterEvent) {
      dateFilterEvent = latestDateEvent;
    } else {
      setLatestDateEvent(dateFilterEvent);
    }

    const filters: PersonUdvalgFilterInput[] = activeFilters.map((set) => {
      return {
        and: [
          {
            udvalgId: {
              eq: set.id,
            },
          },
          {
            periode: dateFilterEvent?.filter,
          },
        ],
      };
    });

    // build the filter string
    const filterString = f<PersonDtoFilterInput>()
      .addFilter("personUdvalg", {
        some: {
          or: filters,
        },
      })
      .build();

    const updatedSettings: IPersonUdvalgFilterSetting = {
      activeFilters: activeFilters,
      dateFilterSettings: dateFilterEvent.settings,
    };

    const queryFilter: QueryFilter<
      PersonFilterKeys,
      IPersonUdvalgFilterSetting
    > = {
      filter: filterString,
      filterKey: filterKey,
      settings: updatedSettings,
      parentFilter: "and",
    };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  function removeFilterHandler(
    id: IPersonUdvalgFilterSetting["activeFilters"][number]["id"],
  ) {
    const updatedFilters =
      settings?.activeFilters?.filter((set) => {
        return id !== set.id;
      }) || [];

    const filters: PersonUdvalgFilterInput[] = updatedFilters.map((set) => {
      return {
        and: [
          {
            udvalgId: {
              eq: set.id,
            },
          },
          {
            periode: latestDateEvent?.filter,
          },
        ],
      };
    });

    // build the filter string
    const filterString = f<PersonDtoFilterInput>()
      .addFilter("personUdvalg", {
        some: {
          or: filters,
        },
      })
      .build();

    const updatedSettings: IPersonUdvalgFilterSetting = {
      activeFilters: updatedFilters,
      dateFilterSettings: latestDateEvent.settings,
    };

    const update = updatedSettings?.activeFilters?.length > 0;

    if (update) {
      updateFilters({
        event: "update",
        queryFilter: {
          filterKey: filterKey,
          filter: filterString,
          settings: updatedSettings,
        },
      });
    } else {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
    }
  }

  return (
    <AccordionFilterItem
      title="Udvalg"
      value="udvalg"
      isActive={filters.some((f) => f.filterKey === filterKey)}
    >
      {settings && (
        <div className="c-applied-filter-group">
          {settings?.activeFilters?.map((filter) => (
            <AppliedFilterItem
              key={filter.id}
              label={filter.name}
              removeFilterHandler={() => removeFilterHandler(filter.id)}
            />
          ))}
        </div>
      )}

      <AutoCompleteUdvalg
        placeholder="Vælg udvalg ..."
        clearAfterSelection
        onSelectedItemChange={(event) => {
          if (event.selectedItem) {
            updateFilterHandler(event.selectedItem);
          }
        }}
      />

      <div className="mt-3">
        <DateFilter
          handleChange={(dateFilterEvent) => {
            updateFilterHandler(null, dateFilterEvent);
          }}
          settings={settings?.dateFilterSettings}
          disabled={isDateDisabled}
          key={JSON.stringify(settings?.dateFilterSettings)}
        />
      </div>
    </AccordionFilterItem>
  );
}
